import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';

import { showHitboxes } from '../compat';
import type { Pose } from './poses';

/**
 * The joints every model exposes, so `poses.ts` can drive any of them without
 * knowing whether it is animating a knight or a pile of bones.
 *
 * Each joint is a `TransformNode` placed at the point the limb pivots around;
 * the geometry hangs off it, offset so it extends away from the pivot. Rotating
 * the joint therefore swings the limb rather than spinning it in place.
 */
export interface Rig {
  root: TransformNode;
  /** Everything below the root except the root itself — bobs and leans. */
  body: TransformNode;
  torso: TransformNode;
  head: TransformNode;
  armR: TransformNode;
  armL: TransformNode;
  legR: TransformNode;
  legL: TransformNode;
  weapon: TransformNode;
  /**
   * How the weapon sits **in the hand**, as opposed to where the hand is.
   *
   * A separate node from `weapon` because the two rotations answer different questions
   * and change at different rates. `weapon` is animated every frame by the pose — it is
   * the *swing*. `grip` is set once when the weapon is built and never touched again: it
   * is how a hand holds that kind of thing.
   *
   * **This is why a sword stopped pointing at the floor.** Everything was built along the
   * hand's own axis and hung straight down it, so a longsword read as a torch held out
   * rather than a blade gripped by the hilt — a hand closes *across* a hilt, so the blade
   * runs roughly perpendicular to the forearm. Folding that into the pose was the
   * alternative, and it would have meant every pose in `poses.ts` knowing which weapon
   * was equipped.
   */
  grip: TransformNode;
  /**
   * The forearms and shins — the joints below the elbow and the knee. Phase 41.
   *
   * Each hangs off its upper limb and carries the lower half of it, so bending the joint
   * folds the limb rather than swinging the whole thing. `poses.ts` writes `elbowR/L` and
   * `kneeR/L` here; `skinned.ts` deliberately does not know about them (its bodies have
   * seven bones) and keeps working, because a rotation nobody forwards is simply not
   * forwarded.
   */
  foreR: TransformNode;
  foreL: TransformNode;
  shinR: TransformNode;
  shinL: TransformNode;
  /** The ends of the forearms. `weapon` hangs from the right one, `offhand` from the left. */
  handR: TransformNode;
  handL: TransformNode;
  /**
   * Where the off hand is — the end of the left forearm — so a shield, a tome, an orb or
   * a second dagger can hang from it exactly as the weapon hangs from `weapon`.
   */
  offhand: TransformNode;
  /**
   * How the hands hold what they hold: a static offset added to the pose every frame.
   *
   * Set once by `buildWeapon` from the profile and never touched again — it is the
   * difference between an empty hand at rest and a hand carrying *this* thing, which the
   * pose maths must not know about (it would mean every pose knowing every weapon). A
   * two-hander's off arm reaches across to the haft; a bow's stays free until the draw.
   */
  hold: HoldOffsets;
  /**
   * Where a two-hander's **second hand** closes on the grip, or null when the off hand is free.
   * Set by `buildWeapon` from `WeaponProfile.offHandGrip`; `applyPose` solves the off arm to it.
   */
  offGrip: TransformNode | null;
  /** How far up the grip (+Y from `offGrip`) the second hand may slide. */
  offGripSpan: number;
  /** Which basic-attack shape this hand wants: a swing, or a draw. */
  holdKind: HoldKind;
  /**
   * The light a caster gathers between their hands, or null for a rig without one.
   *
   * Built into `heldParts` at scale zero and grown by `ProceduralModel.update` through a
   * cast that is not a swing. It is what makes a spell *read* as a spell at 45 device
   * pixels: the eight cast poses were measured apart in radians and still arrived as
   * "arms go up", because at that size a raised arm and a raised arm are the same thing
   * and a point of light is not.
   */
  castGlow: Mesh | null;
  /**
   * The body's geometry: everything, and only everything, the collider is measured
   * from.
   *
   * Anything held in a hand does **not** go in here — see `heldParts`.
   */
  parts: Mesh[];
  /**
   * Geometry held in a hand — the weapon, and the off-hand shield — kept out of
   * `parts` so `measureRig` cannot see it.
   *
   * **A collider is the body.** A held thing is out to one side, it swings, and it is
   * whatever the player equipped: a staff rises 1.68 units above the hand and a
   * greatsword's tip 1.66, so a box containing it would be mostly empty air on the
   * side the weapon happens to be on — and it would change size on every equip, which
   * is exactly the guarantee a collider must not lose. Ten weapon silhouettes spanning
   * 0.36 to 2.38 units of reach make that a two-unit swing in a click target that is
   * supposed to describe a character.
   *
   * Two things this caught, neither of which anything reported: the crusader's original
   * longsword pushed the player's box **0.32 units below the floor they stand on**, and
   * the kite shield widened it by 0.035 — so dropping the shield for a two-hander (the
   * visible half of "a two-hander blocks the off-hand") would otherwise have made the
   * collider depend on the weapon after all. The shield is held, so it is here too, and
   * the invariant is exact rather than nearly true.
   *
   * These meshes are still tagged, still unpickable, still shadow casters and still
   * outlined on hover — `EntityModel.parts` concatenates the two lists. The only thing
   * they are excluded from is the measurement.
   */
  heldParts: Mesh[];
  /** Standing height of the body node, so the bob is relative to it. */
  baseY: number;
}

/** Static per-joint offsets for a grip. Radians, added to the pose in `applyPose`. */
export interface HoldOffsets {
  armRPitch: number;
  armRYaw: number;
  armLPitch: number;
  armLYaw: number;
  elbowR: number;
  elbowL: number;
}

/**
 * How a held thing wants the body to attack with it.
 *
 *  - `swing` — the overhead blow every melee weapon and the default longsword use
 *  - `draw`  — a bow: drawn and loosed, never swung (`drawPose`)
 */
export type HoldKind = 'swing' | 'draw';

export function noHold(): HoldOffsets {
  return { armRPitch: 0, armRYaw: 0, armLPitch: 0, armLYaw: 0, elbowR: 0, elbowL: 0 };
}

/**
 * Limb lengths a rig is built with, so the elbow and knee joints land where the geometry
 * bends. World units, measured down from the joint above.
 */
export interface LimbLengths {
  upperArm: number;
  foreArm: number;
  thigh: number;
  shin: number;
}

/** The crusader's proportions, and the default for anything that does not say. */
export const HUMAN_LIMBS: LimbLengths = { upperArm: 0.24, foreArm: 0.24, thigh: 0.32, shin: 0.32 };

export interface BoxSpec {
  name: string;
  size: [number, number, number];
  /** Centre of the box relative to its parent joint. */
  at: [number, number, number];
  material: StandardMaterial;
  /** Rotation in radians, applied once at build time. */
  rotation?: [number, number, number];
}

/** Builds a box part under `parent` and records it for shadows and picking. */
export function box(scene: Scene, parent: TransformNode, out: Mesh[], spec: BoxSpec): Mesh {
  const mesh = MeshBuilder.CreateBox(
    spec.name,
    { width: spec.size[0], height: spec.size[1], depth: spec.size[2] },
    scene,
  );
  mesh.parent = parent;
  mesh.position.set(spec.at[0], spec.at[1], spec.at[2]);
  if (spec.rotation) mesh.rotation.set(spec.rotation[0], spec.rotation[1], spec.rotation[2]);
  mesh.material = spec.material;
  out.push(mesh);
  return mesh;
}

export interface CylinderSpec {
  name: string;
  height: number;
  diameterTop: number;
  diameterBottom: number;
  at: [number, number, number];
  material: StandardMaterial;
  rotation?: [number, number, number];
  tessellation?: number;
  /** Fraction of the circumference to build, 0 → 1. A hood is a cone with a gap in front. */
  arc?: number;
}

/** Builds a cylinder part. Used for limbs, spines and the hood. */
export function cylinder(
  scene: Scene,
  parent: TransformNode,
  out: Mesh[],
  spec: CylinderSpec,
): Mesh {
  const mesh = MeshBuilder.CreateCylinder(
    spec.name,
    {
      height: spec.height,
      diameterTop: spec.diameterTop,
      diameterBottom: spec.diameterBottom,
      // Low tessellation on purpose: the silhouette should look faceted, and
      // the pixel post-process throws away any smoothness anyway.
      tessellation: spec.tessellation ?? 8,
      arc: spec.arc ?? 1,
    },
    scene,
  );
  mesh.parent = parent;
  mesh.position.set(spec.at[0], spec.at[1], spec.at[2]);
  if (spec.rotation) mesh.rotation.set(spec.rotation[0], spec.rotation[1], spec.rotation[2]);
  mesh.material = spec.material;
  out.push(mesh);
  return mesh;
}

export interface CapsuleSpec {
  name: string;
  /** End to end, caps included. */
  height: number;
  /** Radius at the top and at the bottom, so a limb can taper toward the hand. */
  radiusTop: number;
  radiusBottom: number;
  at: [number, number, number];
  material: StandardMaterial;
  rotation?: [number, number, number];
  tessellation?: number;
}

/**
 * Builds a capsule part — the limb primitive of the Phase 41 rig.
 *
 * **Why a capsule and not a thinner box.** The idea file's *bevelled edges* were declined
 * with the arithmetic (a 0.02-unit chamfer is 0.17 of an output pixel), and that arithmetic
 * still holds; what it does not cover is a *silhouette*. A box limb has four flat faces,
 * so under one directional light two of them are the same shade and the arm reads as a
 * plank. A capsule's shading wraps around it continuously, and its ends are round, so the
 * outline is a rod with a knuckle rather than a stick — and that difference is the whole
 * width of the limb, not the width of a bevel.
 *
 * Eight-sided and lightly subdivided on purpose: roughly 150 triangles a limb, so a body
 * of two dozen parts is under 4 000 triangles, against ~300 for the box rig. A pack of 32
 * is ~120 000, which is a fraction of one frame's budget and what "without compromising
 * performance" means in practice — the draw calls, which are the real cost per part, are
 * unchanged because the part count is.
 */
export function capsule(
  scene: Scene,
  parent: TransformNode,
  out: Mesh[],
  spec: CapsuleSpec,
): Mesh {
  const mesh = MeshBuilder.CreateCapsule(
    spec.name,
    {
      height: spec.height,
      radiusTop: spec.radiusTop,
      radiusBottom: spec.radiusBottom,
      tessellation: spec.tessellation ?? 8,
      subdivisions: 1,
      capSubdivisions: 3,
    },
    scene,
  );
  mesh.parent = parent;
  mesh.position.set(spec.at[0], spec.at[1], spec.at[2]);
  if (spec.rotation) mesh.rotation.set(spec.rotation[0], spec.rotation[1], spec.rotation[2]);
  mesh.material = spec.material;
  out.push(mesh);
  return mesh;
}

export interface SphereSpec {
  name: string;
  /** Extent on each axis — an ellipsoid when they differ. */
  size: [number, number, number];
  at: [number, number, number];
  material: StandardMaterial;
  rotation?: [number, number, number];
  segments?: number;
  /**
   * How much of the sphere to build, 0 → 1 from the top. `0.5` is a dome — a pauldron, a
   * skull cap, a shield boss — and costs half the triangles of the whole.
   */
  slice?: number;
}

/** Builds a sphere or ellipsoid part: heads, joints, pommels, orbs, domes. */
export function sphere(
  scene: Scene,
  parent: TransformNode,
  out: Mesh[],
  spec: SphereSpec,
): Mesh {
  const mesh = MeshBuilder.CreateSphere(
    spec.name,
    {
      diameterX: spec.size[0],
      diameterY: spec.size[1],
      diameterZ: spec.size[2],
      segments: spec.segments ?? 6,
      slice: spec.slice ?? 1,
    },
    scene,
  );
  mesh.parent = parent;
  mesh.position.set(spec.at[0], spec.at[1], spec.at[2]);
  if (spec.rotation) mesh.rotation.set(spec.rotation[0], spec.rotation[1], spec.rotation[2]);
  mesh.material = spec.material;
  out.push(mesh);
  return mesh;
}

export interface WedgeSpec {
  name: string;
  /** Width across the flat, length along the axis, thickness through it. */
  size: [number, number, number];
  /** Width and thickness at the far end as a fraction of the near end; 0 is a point. */
  taper: number;
  /** Point the taper down −Y instead of up. Baked in, so `rotation` still composes freely. */
  flip?: boolean;
  at: [number, number, number];
  material: StandardMaterial;
  rotation?: [number, number, number];
}

/**
 * Builds a tapered, diamond-sectioned part along +Y: a blade, an axe bit, a horn, a spike.
 *
 * A four-sided cylinder is a diamond in plan; scaled thin on one axis and wide on the other
 * it is a blade with two edges and a spine, and `diameterTop` tapers it to a point. That
 * is the shape a sword *is* — the box blade it replaces had a flat 0.04-unit face pointed
 * at the camera and read as a ruler. The scale is baked into the vertices so the mesh's
 * own transform stays clean for the caller to rotate.
 */
export function wedge(
  scene: Scene,
  parent: TransformNode,
  out: Mesh[],
  spec: WedgeSpec,
): Mesh {
  const [width, length, thickness] = spec.size;
  const mesh = MeshBuilder.CreateCylinder(
    spec.name,
    {
      height: length,
      diameterBottom: 1,
      diameterTop: Math.max(0, spec.taper),
      tessellation: 4,
    },
    scene,
  );
  // A 4-gon cylinder of diameter 1 puts its corners on the axes at ±0.5 (measured under
  // `NullEngine`, not assumed), so scaling by the width and the thickness lands the edges
  // exactly where the spec says.
  mesh.scaling.set(width, 1, thickness);
  mesh.bakeCurrentTransformIntoVertices();
  if (spec.flip) {
    mesh.rotation.set(0, 0, Math.PI);
    mesh.bakeCurrentTransformIntoVertices();
  }
  mesh.parent = parent;
  mesh.position.set(spec.at[0], spec.at[1], spec.at[2]);
  if (spec.rotation) mesh.rotation.set(spec.rotation[0], spec.rotation[1], spec.rotation[2]);
  mesh.material = spec.material;
  out.push(mesh);
  return mesh;
}

export interface TorusSpec {
  name: string;
  /** Centre-line diameter of the ring. */
  diameter: number;
  /** Thickness of the ring itself. */
  thickness: number;
  at: [number, number, number];
  material: StandardMaterial;
  rotation?: [number, number, number];
  tessellation?: number;
}

/** Builds a ring part: a belt, a helm's brim, a rib, a shield's rim. Lies flat in XZ. */
export function torus(
  scene: Scene,
  parent: TransformNode,
  out: Mesh[],
  spec: TorusSpec,
): Mesh {
  const mesh = MeshBuilder.CreateTorus(
    spec.name,
    { diameter: spec.diameter, thickness: spec.thickness, tessellation: spec.tessellation ?? 10 },
    scene,
  );
  mesh.parent = parent;
  mesh.position.set(spec.at[0], spec.at[1], spec.at[2]);
  if (spec.rotation) mesh.rotation.set(spec.rotation[0], spec.rotation[1], spec.rotation[2]);
  mesh.material = spec.material;
  out.push(mesh);
  return mesh;
}

export interface TubeSpec {
  name: string;
  /** The centre line, in the parent's space. */
  path: Vector3[];
  radius: number;
  material: StandardMaterial;
  tessellation?: number;
}

/**
 * Builds a part along a curve: a bow limb, a horn, a tail.
 *
 * Built once, so the path is allocated here and nowhere per frame.
 */
export function tube(scene: Scene, parent: TransformNode, out: Mesh[], spec: TubeSpec): Mesh {
  const mesh = MeshBuilder.CreateTube(
    spec.name,
    { path: spec.path, radius: spec.radius, tessellation: spec.tessellation ?? 6, cap: 3 },
    scene,
  );
  mesh.parent = parent;
  mesh.material = spec.material;
  out.push(mesh);
  return mesh;
}

export interface LatheSpec {
  name: string;
  /** Radius at each height, bottom first. Heights are along +Y from `at`. */
  profile: [radius: number, y: number][];
  at: [number, number, number];
  material: StandardMaterial;
  rotation?: [number, number, number];
  tessellation?: number;
}

/**
 * Builds a turned part from a radius-by-height profile: a helm, a goblet, a mace head, a
 * pommel. Closed at both ends.
 */
export function lathe(scene: Scene, parent: TransformNode, out: Mesh[], spec: LatheSpec): Mesh {
  const shape = spec.profile.map(([r, y]) => new Vector3(r, y, 0));
  const mesh = MeshBuilder.CreateLathe(
    spec.name,
    { shape, tessellation: spec.tessellation ?? 8, closed: true, cap: 3 },
    scene,
  );
  mesh.parent = parent;
  mesh.position.set(spec.at[0], spec.at[1], spec.at[2]);
  if (spec.rotation) mesh.rotation.set(spec.rotation[0], spec.rotation[1], spec.rotation[2]);
  mesh.material = spec.material;
  out.push(mesh);
  return mesh;
}

/** Creates a joint node at a point relative to its parent. */
export function joint(
  name: string,
  parent: TransformNode,
  at: [number, number, number],
  scene: Scene,
): TransformNode {
  const node = new TransformNode(name, scene);
  node.parent = parent;
  node.position.set(at[0], at[1], at[2]);
  return node;
}

/**
 * Builds the empty skeleton of joints every model shares.
 *
 * `limbs` places the elbows, knees and hands: the forearm joint sits `upperArm` below the
 * shoulder, the hand `foreArm` below that, and the legs likewise. A builder that draws
 * its own limbs at other lengths passes its own numbers so the joints stay at the bends.
 *
 * `shoulder` and `hip` are the half-spans — how far out from the spine each pair sits.
 */
export function createRig(
  scene: Scene,
  name: string,
  baseY: number,
  limbs: LimbLengths = HUMAN_LIMBS,
  shoulder = 0.34,
  hip = 0.16,
  shoulderY = 0.42,
): Rig {
  const root = new TransformNode(`${name}-root`, scene);
  const body = joint(`${name}-body`, root, [0, baseY, 0], scene);
  const torso = joint(`${name}-torso`, body, [0, 0, 0], scene);

  const armR = joint(`${name}-armR`, torso, [-shoulder, shoulderY, 0], scene);
  const armL = joint(`${name}-armL`, torso, [shoulder, shoulderY, 0], scene);
  const foreR = joint(`${name}-foreR`, armR, [0, -limbs.upperArm, 0], scene);
  const foreL = joint(`${name}-foreL`, armL, [0, -limbs.upperArm, 0], scene);
  const handR = joint(`${name}-handR`, foreR, [0, -limbs.foreArm, 0], scene);
  const handL = joint(`${name}-handL`, foreL, [0, -limbs.foreArm, 0], scene);

  const legR = joint(`${name}-legR`, body, [-hip, 0, 0], scene);
  const legL = joint(`${name}-legL`, body, [hip, 0, 0], scene);

  const weapon = joint(`${name}-weapon`, handR, [0, 0, 0], scene);

  return {
    root,
    body,
    torso,
    head: joint(`${name}-head`, torso, [0, 0.62, 0], scene),
    armR,
    armL,
    foreR,
    foreL,
    handR,
    handL,
    legR,
    legL,
    shinR: joint(`${name}-shinR`, legR, [0, -limbs.thigh, 0], scene),
    shinL: joint(`${name}-shinL`, legL, [0, -limbs.thigh, 0], scene),
    weapon,
    offhand: joint(`${name}-offhand`, handL, [0, 0, 0], scene),
    // Under the weapon node, so the swing and the grip compose: the pose rotates
    // `weapon`, `buildWeapon` rotates this once, and neither knows about the other.
    grip: joint(`${name}-grip`, weapon, [0, 0, 0], scene),
    hold: noHold(),
    offGrip: null,
    offGripSpan: 0,
    holdKind: 'swing',
    castGlow: null,
    parts: [],
    heldParts: [],
    baseY,
  };
}

/**
 * Writes a pose onto a rig.
 *
 * Every assignment is into an existing vector — no `new Vector3` anywhere, so a
 * 60 fps render loop over a dozen entities allocates nothing.
 */
export function applyPose(rig: Rig, pose: Pose): void {
  const h = rig.hold;

  rig.root.rotation.z = pose.rootRoll;
  rig.root.position.y = -pose.rootSink;

  rig.body.position.y = rig.baseY + pose.torsoY;
  rig.torso.rotation.x = pose.torsoPitch;
  rig.torso.rotation.y = pose.torsoYaw;

  rig.head.rotation.x = pose.headPitch;
  rig.head.rotation.y = pose.headYaw;

  // **A hanging limb is negated.** `poses.ts` promises that positive pitch swings a limb
  // *forward* (+Z), and for the torso and head — which point up — Babylon's rotation.x
  // agrees. For a limb that hangs *down* the same rotation goes the other way: measured
  // under `NullEngine`, rotation.x = +0.85 carries (0, −1, 0) to z = −0.75, behind the body.
  // So for the life of the box rig every swing landed behind the character, every wind-up
  // rose in front, and the roll's "knees to chest" kicked backwards — mirrored front to
  // back on a figure 45 device pixels tall, where nobody could tell. Phase 41 put elbows
  // on that arm, and a cocked forearm makes the mirror obvious. The sign lives here, once,
  // rather than in every pose and every pose test.
  rig.armR.rotation.x = -(pose.armRPitch + h.armRPitch);
  rig.armR.rotation.y = pose.armRYaw + h.armRYaw;
  rig.armL.rotation.x = -(pose.armLPitch + h.armLPitch);
  rig.armL.rotation.y = pose.armLYaw + h.armLYaw;
  // The two-hand solve writes a roll here; a posed arm has none.
  rig.armL.rotation.z = 0;
  // Elbows flex forward, knees back; both are stated as forward-positive pitches and take
  // the same negation as the limb they fold.
  rig.foreR.rotation.x = -(pose.elbowR + h.elbowR);
  rig.foreL.rotation.x = -(pose.elbowL + h.elbowL);

  rig.legR.rotation.x = -pose.legRPitch;
  rig.legL.rotation.x = -pose.legLPitch;
  rig.shinR.rotation.x = -pose.kneeR;
  rig.shinL.rotation.x = -pose.kneeL;

  rig.weapon.rotation.x = pose.weaponPitch;

  if (rig.offGrip) {
    // What the pose asked of the off arm, kept so a free-handed spell can take it back.
    posed.copyFrom(rig.armL.rotation);
    const posedElbow = rig.foreL.rotation.x;
    reachOffHand(rig, rig.offGrip);
    const free = pose.offHandFree;
    if (free > 0) {
      Vector3.LerpToRef(rig.armL.rotation, posed, free, rig.armL.rotation);
      rig.foreL.rotation.x += (posedElbow - rig.foreL.rotation.x) * free;
    }
  }
}

// Scratch for `reachOffHand`, so a frame allocates nothing.
const toTorso = new Matrix();
const target = new Vector3();
const bent = new Vector3();
const axis = new Vector3();
const turn = new Quaternion();
const posed = new Vector3();
const spanEnd = new Vector3();
const alongSpan = new Vector3();

/**
 * Puts the off hand **on** a two-hander's grip: a two-bone solve of the left arm to `at`, every
 * frame, after the pose.
 *
 * **Why solved and not posed.** The off arm used to be a fixed offset (`hold`), tuned by eye on
 * the box body so the hand *reached across toward* the haft. It never met it: the grip moves with
 * every walk-cycle swing and every chop, and `alignArms` moves a painted body's shoulder in by
 * 14 cm — so the hand hung at the belt, a hand's width from anything, which the owner saw as
 * *the unarmed hand is a bit awkward in this position, it's not natural*.
 *
 * The elbow angle comes from the distance (the law of cosines, written for the joints' real
 * offsets rather than assumed straight limbs, since `alignArms` leaves them angled); the shoulder
 * then turns the bent arm onto the target by the shortest rotation. Out of reach, the arm points
 * straight at it. Written as Euler angles, because the skinned body copies `rotation` and nothing
 * else onto its bones.
 */
function reachOffHand(rig: Rig, at: TransformNode): void {
  for (const node of [rig.root, rig.body, rig.torso, rig.armR, rig.foreR, rig.handR, rig.weapon, rig.grip, at]) {
    node.computeWorldMatrix(true);
  }
  rig.torso.getWorldMatrix().invertToRef(toTorso);
  // The stretch of grip, in the torso's frame, and the point of it nearest the shoulder.
  Vector3.TransformCoordinatesToRef(at.getAbsolutePosition(), toTorso, target);
  spanEnd.set(0, rig.offGripSpan, 0);
  Vector3.TransformCoordinatesToRef(spanEnd, at.getWorldMatrix(), spanEnd);
  Vector3.TransformCoordinatesToRef(spanEnd, toTorso, spanEnd);
  spanEnd.subtractToRef(target, alongSpan);
  const spanSq = alongSpan.lengthSquared();
  if (spanSq > 1e-8) {
    const f = (Vector3.Dot(rig.armL.position, alongSpan) - Vector3.Dot(target, alongSpan)) / spanSq;
    target.addInPlace(alongSpan.scaleInPlace(Math.max(0, Math.min(1, f))));
  }
  target.subtractInPlace(rig.armL.position);

  // The elbow: |P1 + Rx(θ)·P2| = d, i.e. B·cosθ + C·sinθ = K, for the upper arm P1 and forearm P2.
  const p1 = rig.foreL.position;
  const p2 = rig.handL.position;
  const reach = p1.length() + p2.length();
  const d = Math.min(target.length(), reach * 0.999);
  const b = p1.y * p2.y + p1.z * p2.z;
  const c = p1.z * p2.y - p1.y * p2.z;
  const k = (d * d - p1.lengthSquared() - p2.lengthSquared()) / 2 - p1.x * p2.x;
  const r = Math.hypot(b, c) || 1;
  const phi = Math.atan2(c, b);
  const spread = Math.acos(Math.max(-1, Math.min(1, k / r)));
  // Of the two solutions, the one that folds the forearm forward (a negative rotation.x — see the
  // sign note above).
  const theta = phi - spread <= 0 ? phi - spread : phi + spread;
  rig.foreL.rotation.set(theta, 0, 0);

  // The hand, with the shoulder unturned, in the shoulder's frame: P1 + Rx(θ)·P2.
  const cos = Math.cos(theta);
  const sin = Math.sin(theta);
  bent.set(p1.x + p2.x, p1.y + p2.y * cos - p2.z * sin, p1.z + p2.y * sin + p2.z * cos);

  // The shortest turn carrying that hand onto the target.
  bent.normalize();
  target.normalize();
  Vector3.CrossToRef(bent, target, axis);
  const angle = Math.acos(Math.max(-1, Math.min(1, Vector3.Dot(bent, target))));
  if (axis.lengthSquared() < 1e-10) axis.set(1, 0, 0);
  Quaternion.RotationAxisToRef(axis.normalize(), angle, turn);
  turn.toEulerAnglesToRef(rig.armL.rotation);
}

/** Width and height of an entity's click target, in world units. */
/**
 * How much bigger than the model the collider is, and its floor.
 *
 * Generous on purpose. At the 26-unit gameplay camera a character is roughly
 * 30 × 45 device pixels, so a collider that hugged the silhouette exactly would
 * be a technically honest target that nobody can hit. 0.35 either side puts a
 * skeleton's box at about 1.5 × 2.2 world units — a little over a third wider
 * than the body, which reads as forgiving rather than sloppy.
 *
 * The floors stop a small or oddly-posed model from producing a target a few
 * pixels across.
 */
export const HITBOX_PADDING = 0.35;
export const HITBOX_MIN_WIDTH = 1.2;
export const HITBOX_MIN_HEIGHT = 1.8;

/**
 * Tags every part with its entity id and builds the click target.
 *
 * The visual meshes are made **unpickable** and a single invisible box takes over
 * as the hitbox. Picking against the geometry meant aiming at a rib or a
 * pauldron — targets a few pixels wide at gameplay camera distance, and they move
 * as the model animates, so a click that was on the arm a frame ago misses.
 *
 * **The box is measured from the model, not assumed.** It used to be a hardcoded
 * 1.5 × 2.4 centred at y 1.2 — but a skeleton stands about 1.55 tall, so that box
 * put three quarters of a unit of empty air above its skull and hung its own
 * centre near the model's head. Measuring the built rig means every model, and
 * every future one, gets a collider that matches what the player sees.
 *
 * The collider is invisible, which Babylon's default pick predicate skips, so
 * `attachInput` widens that predicate — see game/input.ts. `?hitboxes=1` draws it.
 *
 * `clickable` is false for players: a collider on your own character would stand
 * between the cursor and the floor beneath you.
 *
 * **Held geometry is tagged and unpickable like everything else, and excluded from the
 * measurement.** `rig.heldParts` says why: a hitbox that grew with a polearm would be
 * longer than the character carrying it, and it would change on every equip.
 *
 * World matrices are deliberately *not* frozen: every part hangs off an animated
 * joint, so Babylon has to recompute them each frame.
 */
export function finaliseParts(
  scene: Scene,
  rig: Rig,
  entityId: string,
  clickable: boolean,
): Mesh {
  for (const part of [...rig.parts, ...rig.heldParts]) {
    part.metadata = { entityId };
    // The hitbox does the picking; geometry that moves with the animation is a
    // terrible thing to aim at.
    part.isPickable = false;
  }

  const bounds = measureRig(rig);

  const hitbox = MeshBuilder.CreateBox(
    `hitbox-${entityId}`,
    { width: bounds.width, height: bounds.height, depth: bounds.depth },
    scene,
  );
  hitbox.parent = rig.root;
  hitbox.position.set(bounds.centreX, bounds.centreY, bounds.centreZ);
  // Only things worth clicking take clicks. Your own character and other
  // players are not targets, and a collider on them would sit between the
  // cursor and the floor — clicking where you are standing would do nothing at
  // all, which reads as the game ignoring you.
  hitbox.isPickable = clickable;
  hitbox.metadata = { entityId };

  hitbox.isVisible = showHitboxes();
  if (hitbox.isVisible) {
    // Wireframe, so the model stays readable underneath it.
    const wire = new StandardMaterial(`hitbox-mat-${entityId}`, scene);
    wire.wireframe = true;
    wire.emissiveColor = new Color3(0.2, 0.9, 0.4);
    wire.disableLighting = true;
    hitbox.material = wire;
  }

  return hitbox;
}

/** The padded extents of a rig's geometry, in the root's local space. */
export interface RigBounds {
  width: number;
  height: number;
  depth: number;
  centreX: number;
  centreY: number;
  centreZ: number;
}

/**
 * Measures a rig's **body** parts in root-local space.
 *
 * `rig.heldParts` is deliberately not walked. A collider is the body; a weapon is held
 * out to one side, and the whole point of one model per weapon type is that a player
 * can change it — so a collider that included it would change size on every equip, and
 * a two-handed polearm would make a character's hitbox longer than the character. This
 * is the same trap that would have made every hitbox plate-sized had the armour layers
 * been toggled rather than rebuilt.
 *
 * The model is measured in whatever pose it was built in, and that is the point:
 * a collider tracking the animation would grow and shrink as the character
 * breathes, and a click that landed a frame ago would miss now.
 *
 * Root-local rather than world, because the collider is parented to the root and
 * inherits its position, rotation and scale — an elite's 1.35× comes for free.
 */
export function measureRig(rig: Rig): RigBounds {
  return measureParts(rig.parts, rig.root);
}

/**
 * The padded extents of some geometry, in a node's local space.
 *
 * `measureRig`'s body, lifted out because **a collider is measured from the model** is
 * not a rule about characters. A town portal's gate and a shopkeeper are click targets
 * too now, and a hand-written box on either drifts off the thing it describes the moment
 * the geometry changes — which is the fault this function exists to make impossible, and
 * the reason the entity box that preceded it sat centred near a skeleton's head.
 *
 * Every bounding-box corner, not two: a rotated part's extremes are not its transformed
 * min and max, and every artisan in town is rotated to face its own street.
 */
export function measureParts(parts: readonly Mesh[], root: TransformNode): RigBounds {
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;

  root.computeWorldMatrix(true);
  const toLocal = Matrix.Invert(root.getWorldMatrix());

  for (const part of parts) {
    part.computeWorldMatrix(true);

    // Every corner, not just two: a rotated part's extremes are not its
    // transformed min and max.
    for (const world of part.getBoundingInfo().boundingBox.vectorsWorld) {
      const local = Vector3.TransformCoordinates(world, toLocal);
      minX = Math.min(minX, local.x);
      maxX = Math.max(maxX, local.x);
      minY = Math.min(minY, local.y);
      maxY = Math.max(maxY, local.y);
      minZ = Math.min(minZ, local.z);
      maxZ = Math.max(maxZ, local.z);
    }
  }

  // An empty rig leaves the bounds at infinity.
  if (!Number.isFinite(minX)) {
    return {
      width: HITBOX_MIN_WIDTH,
      height: HITBOX_MIN_HEIGHT,
      depth: HITBOX_MIN_WIDTH,
      centreX: 0,
      centreY: HITBOX_MIN_HEIGHT / 2,
      centreZ: 0,
    };
  }

  return padBounds(minX, maxX, minY, maxY, minZ, maxZ);
}

/**
 * Applies the padding and the minimum sizes.
 *
 * Pure, and exported, because the awkward part is the interaction between the
 * two: widening a too-small box must grow it around the model's centre rather
 * than dragging it off to one side.
 */
export function padBounds(
  minX: number,
  maxX: number,
  minY: number,
  maxY: number,
  minZ: number,
  maxZ: number,
): RigBounds {
  const axis = (lo: number, hi: number, floor: number) => ({
    size: Math.max(hi - lo + HITBOX_PADDING * 2, floor),
    centre: (lo + hi) / 2,
  });

  const x = axis(minX, maxX, HITBOX_MIN_WIDTH);
  const y = axis(minY, maxY, HITBOX_MIN_HEIGHT);
  const z = axis(minZ, maxZ, HITBOX_MIN_WIDTH);

  return {
    width: x.size,
    height: y.size,
    depth: z.size,
    centreX: x.centre,
    centreY: y.centre,
    centreZ: z.centre,
  };
}
